import AgencyHelper from 'features/Agency/agencyHelper';
import feApi from 'api/feApi';
import { ChargeConstants } from 'features/Charge/chargeHelper';
import { AppDialogHelper } from 'general/components/AppDialog';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import miscHelper from 'general/helpers/miscHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

AgencyWithdrawView.propTypes = {};

const MIN_WITHDRAW_AMOUNT = 50000;
const MAX_WITHDRAW_AMOUNT = 20000000;
const NEW_BANK_ACCOUNT_VALUE = '__new_bank_account__';

function AgencyWithdrawView(props) {
  // MARK: ---- PROPERTIES ----
  const { user } = useSelector((state) => state.auth);
  const [balance, setBalance] = useState(0);
  const agencyId = user?.agencyId || user?.agency?.agencyId;
  const isAgency = user?.role === ChargeConstants.UserRole.AGENCY || Utils.isValid(agencyId);
  const [readyToValidate, setReadyToValidate] = useState(false);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [bankAccountList, setBankAccountList] = useState([]);
  const [bankList, setBankList] = useState([]);
  const [pendingWithdrawAmount, setPendingWithdrawAmount] = useState(0);
  const [bankAccountMode, setBankAccountMode] = useState(NEW_BANK_ACCOUNT_VALUE);
  const [bankForm, setBankForm] = useState({
    bankCode: '',
    bankName: '',
    bankAccount: '',
    bankBeneficiary: '',
  });

  const getProfileBankAccounts = () => {
    const agencyBankAccounts = Array.isArray(user?.agency?.bankAccounts)
      ? user.agency.bankAccounts
      : [];
    const profileBankAccount = user?.bankAccount && user?.bankName && user?.bankBeneficiary
      ? [{
        bankAccountId: 'profile',
        bankCode: user?.bankCode || user?.bankName,
        bankName: user?.bankName,
        bankAccount: user?.bankAccount,
        bankBeneficiary: user?.bankBeneficiary,
        defaultAccount: 1,
        status: 'VALIDATED',
      }]
      : [];
    return [...agencyBankAccounts, ...profileBankAccount]
      .filter((item) => item?.bankAccount && item?.bankName && item?.bankBeneficiary);
  };

  const mergeBankAccounts = (items) => {
    const unique = new Map();
    (items || []).forEach((item) => {
      const status = String(item?.status || '').toUpperCase();
      if (['DELETED', 'DISABLED', 'INACTIVE', 'INVALID'].includes(status)) return;
      const bankCode = String(item?.bankCode || item?.bankName || '').trim().toUpperCase();
      const bankName = String(item?.bankName || item?.bankCode || '').trim();
      const bankAccount = String(item?.bankAccount || '').trim();
      const bankBeneficiary = String(item?.bankBeneficiary || '').trim();
      if (!bankName || !bankAccount || !bankBeneficiary) return;
      const key = `${bankCode || bankName.toUpperCase()}|${bankAccount}`;
      if (unique.has(key)) return;
      unique.set(key, {
        ...item,
        bankAccountId: item?.bankAccountId || key,
        bankCode,
        bankName,
        bankAccount,
        bankBeneficiary,
      });
    });
    return Array.from(unique.values());
  };

  const loadBankAccounts = () => {
    if (!isAgency || !agencyId) return;
    AgencyHelper.API.requestGetAgencyBankAccountList(agencyId).then((response) => {
      setBankAccountList(mergeBankAccounts([
        ...(response.bankAccounts || []),
        ...getProfileBankAccounts(),
      ]));
    });
  };

  const loadBankList = () => {
    miscHelper.callApiGetBankList().then((banks) => {
      setBankList(Array.isArray(banks) ? banks : []);
    });
  };

  const loadAgencyBalance = () => {
    if (!isAgency || !agencyId) return;
    feApi.getAffiliateSummary().then((response) => {
      if (response?.result === 'success') {
        setBalance(Number(response?.summary?.availableCommission || 0));
        setPendingWithdrawAmount(Number(response?.summary?.withdrawalPending || 0));
      }
    }).catch(() => {
      AgencyHelper.API.requestAgencyDetail(agencyId).then((agency) => {
        if (Utils.isValid(agency)) {
          setBalance(Number(agency?.balance || 0));
        }
      });
    });
  };

  const loadPendingWithdrawAmount = () => {
    if (!isAgency || !agencyId) return;
    feApi.getAffiliateSummary().then((response) => {
      if (response?.result === 'success') {
        setPendingWithdrawAmount(Number(response?.summary?.withdrawalPending || 0));
      }
    }).catch(() => {
      Promise.all([
        AgencyHelper.API.requestGetAgencyWithdrawRequests({
          agencyId,
          status: 'REQUESTED',
          limit: 100,
        }),
        AgencyHelper.API.requestGetAgencyWithdrawRequests({
          agencyId,
          status: 'PROCESSING',
          limit: 100,
        }),
      ]).then(([requested, processing]) => {
        const payments = [
          ...(requested?.payments || []),
          ...(processing?.payments || []),
        ];
        setPendingWithdrawAmount(_.sumBy(payments, (item) => Number(item?.amount || 0)));
      });
    });
  };

  useEffect(() => {
    if (isAgency) {
      loadAgencyBalance();
      loadBankAccounts();
      loadPendingWithdrawAmount();
      loadBankList();
    }
  }, [user, isAgency]);
  const availableBalance = Math.max(Number(balance || 0), 0);
  const bankAccountOptions = useMemo(() => {
    return [
      ...bankAccountList.map((item) => ({
        name: `${item.bankAccount} - ${item.bankName || item.bankCode || ''}`,
        value: item.bankAccountId,
      })),
      {
        name: bankAccountList.length > 0 ? 'Thêm tài khoản ngân hàng mới' : 'Nhập tài khoản ngân hàng',
        value: NEW_BANK_ACCOUNT_VALUE,
      },
    ];
  }, [bankAccountList]);
  const selectedBankAccountValue = Utils.isValid(selectedBankAccount)
    ? selectedBankAccount.bankAccountId
    : NEW_BANK_ACCOUNT_VALUE;
  const isNewBankAccount = bankAccountMode === NEW_BANK_ACCOUNT_VALUE || !Utils.isValid(selectedBankAccount);
  const withdrawBankAccount = isNewBankAccount
    ? {
      bankCode: String(bankForm.bankCode || '').trim().toUpperCase(),
      bankName: String(bankForm.bankName || '').trim(),
      bankAccount: String(bankForm.bankAccount || '').replace(/\s+/g, ''),
      bankBeneficiary: String(bankForm.bankBeneficiary || '').trim(),
    }
    : selectedBankAccount;
  const updateBankForm = (field, value) => {
    setBankForm((prev) => ({
      ...prev,
      [field]: field === 'bankCode' ? String(value || '').toUpperCase() : value,
    }));
  };
  useEffect(() => {
    const defaultBankAccount = _.find(bankAccountList, (item) => item.defaultAccount === 1) || _.first(bankAccountList);
    if (Utils.isValid(defaultBankAccount)) {
      setSelectedBankAccount(defaultBankAccount);
      setBankAccountMode(defaultBankAccount.bankAccountId);
    } else {
      setSelectedBankAccount(null);
      setBankAccountMode(NEW_BANK_ACCOUNT_VALUE);
    }
  }, [bankAccountList]);
  const handleBankAccountChange = (value) => {
    if (value === NEW_BANK_ACCOUNT_VALUE) {
      setSelectedBankAccount(null);
      setBankAccountMode(NEW_BANK_ACCOUNT_VALUE);
      return;
    }
    const bankAccount = _.find(bankAccountList, (item) => String(item.bankAccountId) === String(value));
    setSelectedBankAccount(bankAccount);
    setBankAccountMode(value);
  };

  // MARK: ---- FUNCTIONS ----
  const handleSubmit = async () => {
    setReadyToValidate(true);
    if (!Utils.isValid(amount)) {
      ToastHelper.showError('Vui lòng nhập số tiền');
      return;
    }
    if (_.toNumber(amount) < MIN_WITHDRAW_AMOUNT || _.toNumber(amount) > MAX_WITHDRAW_AMOUNT) {
      ToastHelper.showError('Số tiền phải nằm trong khoảng 50.000đ đến 20.000.000đ');
      return;
    }
    if (_.toNumber(amount) > availableBalance) {
      ToastHelper.showError('Số tiền rút không được lớn hơn số dư khả dụng');
      return;
    }
    if (!withdrawBankAccount.bankCode || !withdrawBankAccount.bankName || !withdrawBankAccount.bankAccount || !withdrawBankAccount.bankBeneficiary) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin tài khoản thụ hưởng');
      return;
    }
    const selectedBank = bankList.find((bank) => String(bank?.bankCode || '').toUpperCase() === String(withdrawBankAccount.bankCode || '').toUpperCase());
    if (!selectedBank) {
      ToastHelper.showError(`Mã ngân hàng ${withdrawBankAccount.bankCode} không hợp lệ, vui lòng chọn ngân hàng trong danh sách`);
      return;
    }
    setLoading(true);
    let bankAccountToWithdraw = withdrawBankAccount;
    if (isNewBankAccount) {
      const createdBankAccount = await AgencyHelper.API.requestCreateAgencyBankAccount({
        agencyId,
        bankCode: withdrawBankAccount.bankCode,
        bankName: withdrawBankAccount.bankName,
        bankAccount: withdrawBankAccount.bankAccount,
        bankBeneficiary: withdrawBankAccount.bankBeneficiary,
        defaultAccount: bankAccountList.length === 0 ? 1 : 0,
      });
      if (!Utils.isValid(createdBankAccount) || _.isString(createdBankAccount)) {
        setLoading(false);
        ToastHelper.showError(createdBankAccount || 'Không lưu được tài khoản ngân hàng');
        return;
      }
      bankAccountToWithdraw = createdBankAccount;
      setSelectedBankAccount(createdBankAccount);
      setBankAccountMode(createdBankAccount.bankAccountId);
      loadBankAccounts();
    }
    let res = null;
    try {
      res = await feApi.createAffiliateWithdrawal({
        amount: Number(amount),
        bankCode: bankAccountToWithdraw.bankCode,
        bankAccountNumber: bankAccountToWithdraw?.bankAccount,
        bankName: bankAccountToWithdraw?.bankName,
        accountHolder: bankAccountToWithdraw?.bankBeneficiary,
      })
    } catch (error) {
      res = error?.response?.data || { reason: 'Gửi yêu cầu thất bại.' };
    }
    setLoading(false);
    if (res?.result === 'success') {
      AppDialogHelper.show(
        'Gửi yêu cầu thành công',
        'Giao dịch rút tiền đang được xử lý, chúng tôi sẽ phê duyệt giao dịch này trong thời gian sớm nhất.',
        [
          {
            title: 'Tôi đã hiểu',
            type: 'neutral',
            onClick: () => {
              setAmount('');
              setReadyToValidate(false);
              setBankForm({
                bankCode: '',
                bankName: '',
                bankAccount: '',
                bankBeneficiary: '',
              });
              loadPendingWithdrawAmount();
              loadAgencyBalance();
              AppDialogHelper.hide();
            },
          },
        ],
        false,
        AppResource.images.dialogIcons.dialogSuccess
      )
    } else {
      ToastHelper.showError(res?.reason || 'Gửi yêu cầu thất bại.')
    }
  };

  return (
    <div className="px-4 my-4">
      {UIHelper.buildCard(
        <div>
          <div className="pb-4">
            {/* header */}
            <div
              className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between"
              style={{
                borderBottom: `1px solid ${AppColors.gray[300]}`,
              }}
            >
              {/* title */}
              <div className="d-flex flex-column">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Rút tiền về tài khoản
                </span>
              </div>
            </div>
            {/* body */}
            <div className="pt-4">
              <div className="px-4">
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[900])}>
                  Số dư khả dụng:{' '}
                  <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                    {Utils.formatNumber(availableBalance)} VNĐ
                  </span>
                </span>
                <div className="mt-2">
                  <span style={UIHelper.buildFontStyle('500', '14px', AppColors.gray[700])}>
                    Đã giữ cho lệnh rút đang xử lý: {Utils.formatNumber(pendingWithdrawAmount)} VNĐ
                  </span>
                </div>
              </div>
              <div
                className="mt-2 row m-0 p-0 px-2"
                style={{
                  borderBottom: `1px dashed ${AppColors.gray[300]}`,
                }}
              >
                <div className="col-lg-4 mb-2 px-2">
                  {UIHelper.buildFormLabel('Số tiền muốn rút', true)}
                  <KTFormInput
                    type={KTFormInputType.currency}
                    placeholder="Nhập số"
                    name="amount"
                    value={amount}
                    onChange={(value) => {
                      setAmount(value);
                    }}
                    enableCheckValid={readyToValidate}
                    isValid={
                      Utils.isValid(amount) &&
                      _.toNumber(amount) >= MIN_WITHDRAW_AMOUNT &&
                      _.toNumber(amount) <= MAX_WITHDRAW_AMOUNT &&
                      _.toNumber(amount) <= availableBalance
                    }
                    isTouched={true}
                    feedbackText={
                      !Utils.isValid(amount)
                        ? 'Vui lòng nhập số tiền'
                        : _.toNumber(amount) < MIN_WITHDRAW_AMOUNT || _.toNumber(amount) > MAX_WITHDRAW_AMOUNT
                          ? 'Số tiền không hợp lệ'
                          : _.toNumber(amount) > availableBalance
                            ? 'Số tiền vượt quá số dư khả dụng'
                          : ''
                    }
                  />
                  <div className="mt-1">
                    {UIHelper.buildFormText('Tối thiểu: 50.000 ₫/ Tối đa: 20.000.000 ₫')}
                  </div>
                </div>
              </div>

              {/* tai khoan thu huong */}
              <div className="pt-4" style={{ borderBottom: `1px dashed ${AppColors.gray[300]}` }}>
                <span
                  className="px-4"
                  style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[900])}
                >
                  Tài khoản thụ hưởng
                </span>
                <div className="row m-0 p-0 px-2 mt-2">
                  <div className="col-lg-4 mb-4 px-2">
                    {UIHelper.buildFormLabel('Số tài khoản', true)}
                    <KTBSDropdown
                      name="bankAccountNumber"
                      toggleElement={
                        <button
                          className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between ${readyToValidate && !Utils.isValid(selectedBankAccount)
                            && !isNewBankAccount ? 'button-invalid'
                            : ''
                            }`}
                          type="button"
                        >
                          {isNewBankAccount ? 'Nhập tài khoản ngân hàng' : selectedBankAccount?.bankAccount}
                        </button>
                      }
                      selectedValue={selectedBankAccountValue}
                      dropdownMenuItems={bankAccountOptions}
                      dropdownMenuClassName="overflow-auto w-100"
                      dropdownMenuStyle={{ maxHeight: '30vh' }}
                      onChange={handleBankAccountChange}
                      isDropdownForm={true}
                    />
                  </div>
                  {!isNewBankAccount && (
                    <>
                      <div className="col-lg-4 mb-4 px-2">
                        {UIHelper.buildFormLabel('Ngân hàng', true)}
                        <KTFormInput
                          type={KTFormInputType.text}
                          placeholder="Ngân hàng"
                          name="bankName"
                          value={
                            Utils.isValid(selectedBankAccount)
                              ? [selectedBankAccount.bankName, selectedBankAccount.bankCode].join(' - ')
                              : ''
                          }
                          disabled={true}
                        />
                      </div>
                      <div className="col-lg-4 mb-4 px-2">
                        {UIHelper.buildFormLabel('Người thụ hưởng', true)}
                        <KTFormInput
                          type={KTFormInputType.text}
                          placeholder="Người thụ hưởng"
                          name="bankAccountName"
                          value={
                            Utils.isValid(selectedBankAccount)
                              ? selectedBankAccount.bankBeneficiary
                              : ''
                          }
                          disabled={true}
                        />
                      </div>
                    </>
                  )}
                  {isNewBankAccount && (
                    <>
                      <div className="col-lg-4 mb-4 px-2">
                        {UIHelper.buildFormLabel('Mã ngân hàng', true)}
                        <KTFormInput
                          type={KTFormInputType.text}
                          placeholder="VD: BIDV, VCB, TCB"
                          name="bankCode"
                          value={bankForm.bankCode}
                          onChange={(value) => updateBankForm('bankCode', value)}
                          enableCheckValid={readyToValidate}
                          isValid={Utils.isValid(bankForm.bankCode)}
                          isTouched={true}
                          feedbackText={!Utils.isValid(bankForm.bankCode) ? 'Vui lòng nhập mã ngân hàng' : ''}
                        />
                      </div>
                      <div className="col-lg-4 mb-4 px-2">
                        {UIHelper.buildFormLabel('Tên ngân hàng', true)}
                        <KTFormInput
                          type={KTFormInputType.text}
                          placeholder="Ngân hàng"
                          name="newBankName"
                          value={bankForm.bankName}
                          onChange={(value) => updateBankForm('bankName', value)}
                          enableCheckValid={readyToValidate}
                          isValid={Utils.isValid(bankForm.bankName)}
                          isTouched={true}
                          feedbackText={!Utils.isValid(bankForm.bankName) ? 'Vui lòng nhập tên ngân hàng' : ''}
                        />
                      </div>
                      <div className="col-lg-4 mb-4 px-2">
                        {UIHelper.buildFormLabel('Số tài khoản', true)}
                        <KTFormInput
                          type={KTFormInputType.text}
                          placeholder="Số tài khoản"
                          name="newBankAccount"
                          value={bankForm.bankAccount}
                          onChange={(value) => updateBankForm('bankAccount', value)}
                          enableCheckValid={readyToValidate}
                          isValid={Utils.isValid(bankForm.bankAccount)}
                          isTouched={true}
                          feedbackText={!Utils.isValid(bankForm.bankAccount) ? 'Vui lòng nhập số tài khoản' : ''}
                        />
                      </div>
                      <div className="col-lg-4 mb-4 px-2">
                        {UIHelper.buildFormLabel('Người thụ hưởng', true)}
                        <KTFormInput
                          type={KTFormInputType.text}
                          placeholder="Người thụ hưởng"
                          name="newBankBeneficiary"
                          value={bankForm.bankBeneficiary}
                          onChange={(value) => updateBankForm('bankBeneficiary', value)}
                          enableCheckValid={readyToValidate}
                          isValid={Utils.isValid(bankForm.bankBeneficiary)}
                          isTouched={true}
                          feedbackText={!Utils.isValid(bankForm.bankBeneficiary) ? 'Vui lòng nhập người thụ hưởng' : ''}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* actions */}
              <div className="d-flex align-items-center px-4 mt-4">
                {UIHelper.buildGreenButton('Xác nhận rút tiền', handleSubmit, null, false, loading)}
              </div>
            </div>
          </div>
        </div>,
        false
      )}
    </div>
  );
}

export default AgencyWithdrawView;
